import React from 'react'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'
import { EnrichedIssueEntity } from 'types/IssueEntity'
import { FactsTableRow } from '../../components/FactsTable'
import { DynamicIconButton } from '../../../utils/tooltip/DynamicIconButton'
import { formatBigMoney, formatMoney } from '../../../packages/utils/numberFormatters'
import * as sharesOrUnits from '../../../utils/issues/sharesOrUnits.js'
import InfoIcon from '@mui/icons-material/Info'

export function VolumeRow({ issue }: { issue: EnrichedIssueEntity }) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language
    let volume: string | null = null
    let size: string | null = null

    const isIpoWithIntervalSubPrice =
        issue.type === 'IPO' && !issue.unitSubPrice && issue.ipoIntervalHigh !== null && issue.ipoIntervalLow !== null
    const totalOfferedUnits = issue.totalOfferedUnits ?? 0

    if (issue.sharesInUnit && issue.sharesInUnit > 1) {
        const shares = totalOfferedUnits * issue.sharesInUnit
        volume = t('pages.issues.terms.volumeCorresponding', {
            units: sharesOrUnits.unitsQuantityLabel(totalOfferedUnits, locale),
            shares: sharesOrUnits.sharesQuantityLabel(shares, locale),
        })
        size = formatBigMoney(issue.offeredSize, { locale, currencySymbol: issue.currency, decimals: 0 })
    } else {
        volume = sharesOrUnits.quantityLabel(issue, totalOfferedUnits, locale)

        if (isIpoWithIntervalSubPrice) {
            const ipoIntervalLow = parseFloat(issue.ipoIntervalLow)
            const ipoIntervalHigh = parseFloat(issue.ipoIntervalHigh)

            size = `${formatBigMoney((totalOfferedUnits * (ipoIntervalHigh + ipoIntervalLow)) / 2, {
                locale,
                currencySymbol: issue.currency,
                decimals: 0,
            })}*`
        } else {
            size = formatBigMoney(issue.offeredSize, {
                locale,
                currencySymbol: issue.currency,
                decimals: 0,
                tooltip: true,
            })
        }
    }
    const label = (() => {
        if (issue.type === 'Rights issue') {
            return t('pages.issues.terms.offeredVolume')
        } else if (issue.type === 'IPO') {
            return t('pages.issues.terms.newSharesIssued')
        }
        return t('pages.issues.terms.volume')
    })()

    const content = (() => {
        if (issue.totalOfferedUnits === null && issue.abbTarget !== null) {
            return formatBigMoney(issue.abbTarget, {
                locale,
                currencySymbol: issue.currency,
                decimals: 0,
                tooltip: true,
            })
        } else if (issue.totalOfferedUnits === 0) {
            return t('pages.issues.terms.noNewSharesIssued')
        } else if (!issue.unitSubPrice && !isIpoWithIntervalSubPrice) {
            return t('pages.issues.terms.notDeterminedYet')
        }

        const offeredUnitsUnlistedLabel =
            issue.offeredUnitsUnlisted !== null && issue.offeredUnitsUnlisted > 0
                ? t('pages.issues.terms.unlistedSharesNote', {
                      quantity: sharesOrUnits.quantityLabel(issue, issue.offeredUnitsUnlisted, locale),
                  })
                : null
        return (
            <>
                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {volume}
                    {offeredUnitsUnlistedLabel ? (
                        <DynamicIconButton
                            title={offeredUnitsUnlistedLabel}
                            IconComponent={InfoIcon}
                            placement='top'
                            padding='0px 4px'
                        />
                    ) : null}
                </Stack>
                {size}
            </>
        )
    })()

    return <FactsTableRow label={label}>{content}</FactsTableRow>
}
